import Tooltip from '@mui/material/Tooltip';
import React from 'react';
import { formatDateAndTimeTextually } from '../../utils/formatDate';

// Generate consistent color based on user ID
export const getAvatarBackgroundColor = (userId: string, isBot: boolean = false, isClient: boolean = false) => {
    if (isBot) {
        return '#7c5cdb'; // Fixed color for bots
    }

    if (isClient) {
        return '#4a9eff'; // Fixed color for clients
    }

    // Generate color from user ID for reps
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
        hash = userId.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Generate HSL color with good saturation and lightness for readability
    const hue = Math.abs(hash % 360);
    const saturation = 65; // Good saturation for vibrant colors
    const lightness = 50; // Medium lightness for good contrast

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

interface AssignedBy {
    _id: string;
    firstName: string;
    lastName: string;
    fullName?: string;
}

interface AssignedBot {
    botName: string;
    assignedBy?: AssignedBy;
    createdAt?: Date;
}

// Get bot avatar content (initials or robot icon)
export const getBotAvatarContent = (assignedBy?: AssignedBy) => {
    if (assignedBy) {
        const initials = assignedBy.firstName.substring(0, 1) + assignedBy.lastName.substring(0, 1);
        const bgColor = getAvatarBackgroundColor(assignedBy._id);
        return (
            <span className="rep" style={{ backgroundColor: bgColor }}>
                {initials}
            </span>
        );
    }
    const botBgColor = getAvatarBackgroundColor('bot', true);
    return (
        <span className="rep" style={{ backgroundColor: botBgColor }}>
            <i className="mdi mdi-robot-outline" style={{ fontSize: '24px' }}></i>
        </span>
    );
};

// Get bot avatar tooltip
export const getBotAvatarTooltip = (assignedBy?: AssignedBy, createdAt?: Date) => {
    if (assignedBy && createdAt) {
        const name = assignedBy.fullName || `${assignedBy.firstName} ${assignedBy.lastName}`;
        const time = formatDateAndTimeTextually(new Date(createdAt));
        return `${name} (בוט) - ${time}`;
    } else if (createdAt) {
        const time = formatDateAndTimeTextually(new Date(createdAt));
        return `Better Bot - ${time}`;
    }
    return 'Better Bot';
};

// Get bot display name with icon
export const getBotDisplayName = (botName: string, messageId: number, assignedBy?: AssignedBy, createdAt?: Date) => {
    const baseName = `${botName} #${messageId}`;

    let tooltipText = 'Triggered automatically';
    let iconClass = 'mdi mdi-robot-outline';

    if (assignedBy && createdAt) {
        const assignedByName = assignedBy.fullName || `${assignedBy.firstName} ${assignedBy.lastName}`;
        const assignedDate = formatDateAndTimeTextually(new Date(createdAt));
        tooltipText = `Triggered by ${assignedByName} on ${assignedDate}`;
        iconClass = 'mdi mdi-account-outline';
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontWeight: 500 }}>{baseName}</span>
            <Tooltip title={tooltipText} arrow>
                <i className={iconClass} style={{ fontSize: '16px', cursor: 'pointer', opacity: 0.7 }}></i>
            </Tooltip>
        </div>
    );
};
